import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { inquiryApi, InquiryResponse } from '../services/inquiryApi';
import '../styles/AdminInquiriesPage.css';

const SearchIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const InboxIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);

const AdminInquiriesPage: React.FC = () => {
  const [inquiries, setInquiries] = useState<InquiryResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedInquiry, setSelectedInquiry] = useState<InquiryResponse | null>(null);
  const [inquiryToDelete, setInquiryToDelete] = useState<InquiryResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showNotification = useCallback((message: string) => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
    }
    setSuccessMessage(message);
    successTimerRef.current = setTimeout(() => {
      setSuccessMessage(null);
    }, 4500);
  }, []);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await inquiryApi.getInquiries();
    setLoading(false);

    if (result.success && result.data) {
      setInquiries(result.data);
    } else {
      setError(result.error || 'Failed to load inquiries.');
    }
  }, []);

  useEffect(() => {
    fetchInquiries();
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, [fetchInquiries]);

  // Handle ESC key to close open modals & manage body overflow
  useEffect(() => {
    const isModalOpen = Boolean(selectedInquiry || inquiryToDelete);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!isDeleting) {
          setSelectedInquiry(null);
          setInquiryToDelete(null);
        }
      }
    };

    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedInquiry, inquiryToDelete, isDeleting]);

  const handleDeleteConfirm = async () => {
    if (!inquiryToDelete) return;

    setIsDeleting(true);
    setError(null);
    const result = await inquiryApi.deleteInquiry(inquiryToDelete.id);
    setIsDeleting(false);

    if (result.success) {
      setInquiries((prev) => prev.filter((item) => item.id !== inquiryToDelete.id));
      showNotification(`Inquiry from "${inquiryToDelete.name}" was deleted successfully.`);
      if (selectedInquiry?.id === inquiryToDelete.id) {
        setSelectedInquiry(null);
      }
      setInquiryToDelete(null);
    } else {
      setError(result.error || 'Failed to delete inquiry.');
      setInquiryToDelete(null);
    }
  };

  const filteredInquiries = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return inquiries;

    return inquiries.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.email.toLowerCase().includes(q) ||
        (item.phone && item.phone.toLowerCase().includes(q)) ||
        (item.companyName && item.companyName.toLowerCase().includes(q)) ||
        item.message.toLowerCase().includes(q)
    );
  }, [inquiries, searchQuery]);

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="inquiries-container">
      <header className="inquiries-header">
        <div className="inquiries-title-area">
          <h1>
            Customer Inquiries
            {!loading && (
              <span className="inquiries-count-badge" title="Total inquiries">
                {searchQuery ? `${filteredInquiries.length} of ${inquiries.length}` : inquiries.length}
              </span>
            )}
          </h1>
          <p className="inquiries-subtitle">
            View and manage customer messages submitted via the Contact Us form.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-small"
          onClick={fetchInquiries}
          disabled={loading}
          aria-label="Refresh inquiries list"
        >
          {loading ? 'Refreshing...' : 'Refresh List'}
        </button>
      </header>

      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success" role="alert">
          {successMessage}
        </div>
      )}

      <div className="inquiries-controls">
        <div className="inquiries-search-box">
          <span className="inquiries-search-icon">
            <SearchIcon />
          </span>
          <input
            type="search"
            className="inquiries-search-input"
            placeholder="Search by name, email, phone, or message..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search inquiries"
          />
        </div>
      </div>

      <div className="inquiries-card">
        {loading ? (
          <div className="inquiries-empty-state">
            <p>Loading inquiries, please wait...</p>
          </div>
        ) : filteredInquiries.length === 0 ? (
          <div className="inquiries-empty-state">
            <InboxIcon />
            <h3>No inquiries found</h3>
            <p>
              {searchQuery
                ? 'No inquiries matched your search criteria.'
                : 'There are currently no customer inquiries in the database.'}
            </p>
          </div>
        ) : (
          <div className="inquiries-table-responsive">
            <table className="inquiries-table">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Mobile</th>
                  <th scope="col">Message Preview</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInquiries.map((item) => (
                  <tr key={item.id}>
                    <td className="inquiry-date">{formatDate(item.createdDate)}</td>
                    <td className="inquiry-name">
                      <div>{item.name}</div>
                      {item.companyName && (
                        <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 'normal' }}>
                          {item.companyName}
                        </div>
                      )}
                    </td>
                    <td className="inquiry-email">
                      <a href={`mailto:${item.email}`}>{item.email}</a>
                    </td>
                    <td className="inquiry-phone">
                      {item.phone ? <a href={`tel:${item.phone}`}>{item.phone}</a> : '—'}
                    </td>
                    <td className="inquiry-message-cell">
                      <div className="inquiry-message-snippet">{item.message}</div>
                    </td>
                    <td>
                      <div className="inquiry-actions">
                        <button
                          type="button"
                          className="btn-action-view"
                          onClick={() => setSelectedInquiry(item)}
                          aria-label={`View full inquiry from ${item.name}`}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="btn-action-delete"
                          onClick={() => setInquiryToDelete(item)}
                          aria-label={`Delete inquiry from ${item.name}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inquiry Detail Modal */}
      {selectedInquiry && (
        <div
          className="inquiry-modal-backdrop"
          onClick={() => setSelectedInquiry(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="inquiry-detail-title"
        >
          <div className="inquiry-modal" onClick={(e) => e.stopPropagation()}>
            <div className="inquiry-modal-header">
              <h2 id="inquiry-detail-title">Inquiry Details</h2>
              <button
                type="button"
                className="inquiry-modal-close"
                onClick={() => setSelectedInquiry(null)}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>
            <div className="inquiry-modal-body">
              <div className="inquiry-detail-row">
                <span className="inquiry-detail-label">Received Date</span>
                <span className="inquiry-detail-value">{formatDate(selectedInquiry.createdDate)}</span>
              </div>
              <div className="inquiry-detail-row">
                <span className="inquiry-detail-label">Sender Name</span>
                <strong className="inquiry-detail-value">{selectedInquiry.name}</strong>
              </div>
              {selectedInquiry.companyName && (
                <div className="inquiry-detail-row">
                  <span className="inquiry-detail-label">Company Name</span>
                  <span className="inquiry-detail-value">{selectedInquiry.companyName}</span>
                </div>
              )}
              <div className="inquiry-detail-row">
                <span className="inquiry-detail-label">Email Address</span>
                <span className="inquiry-detail-value">
                  <a href={`mailto:${selectedInquiry.email}`} style={{ color: '#0d9488' }}>
                    {selectedInquiry.email}
                  </a>
                </span>
              </div>
              <div className="inquiry-detail-row">
                <span className="inquiry-detail-label">Mobile Number</span>
                <span className="inquiry-detail-value">
                  {selectedInquiry.phone ? (
                    <a href={`tel:${selectedInquiry.phone}`} style={{ color: '#0d9488' }}>
                      {selectedInquiry.phone}
                    </a>
                  ) : (
                    'Not provided'
                  )}
                </span>
              </div>
              <div className="inquiry-detail-row">
                <span className="inquiry-detail-label">Full Message</span>
                <div className="inquiry-detail-message-box">{selectedInquiry.message}</div>
              </div>
            </div>
            <div className="inquiry-modal-footer">
              <a
                href={`mailto:${selectedInquiry.email}?subject=${encodeURIComponent(
                  'Re: Inquiry via CertificationISO'
                )}`}
                className="btn btn-primary btn-small"
              >
                Reply by Email
              </a>
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={() => setSelectedInquiry(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {inquiryToDelete && (
        <div
          className="inquiry-modal-backdrop"
          onClick={() => !isDeleting && setInquiryToDelete(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="inquiry-delete-title"
        >
          <div className="inquiry-modal" onClick={(e) => e.stopPropagation()}>
            <div className="inquiry-modal-header">
              <h2 id="inquiry-delete-title">Confirm Delete</h2>
              <button
                type="button"
                className="inquiry-modal-close"
                onClick={() => !isDeleting && setInquiryToDelete(null)}
                disabled={isDeleting}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>
            <div className="inquiry-modal-body">
              <p>
                Are you sure you want to delete the inquiry from <strong>{inquiryToDelete.name}</strong> ({inquiryToDelete.email})?
              </p>
              <p style={{ color: '#64748b', fontSize: '0.88rem' }}>
                This action cannot be undone. The inquiry will be permanently removed from the database.
              </p>
            </div>
            <div className="inquiry-modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={() => setInquiryToDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger btn-small"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInquiriesPage;

